// QuestionPreview — render visual fiel ao QuestionCard do aluno, porém
// 100% read-only e SEM dependências de runtime do student-new (sem
// `api`, sem `signals` globais, sem essay grading). Pensado pra:
//   - manager-new: prévia de banco em /manager/bancos (criar/editar)
//   - teacher-new: prévia em revisão pré-publicação
//   - qualquer painel que queira mostrar uma questão como o aluno veria
//
// Recebe um shape "tolerante" de questão (várias chaves opcionais) — assim
// rola tanto com o JSON cru do upload quanto com o que o backend devolve em
// `QuestaoStudy`. Render é estritamente visual; KaTeX é carregado via
// `ensureKaTeX` + `renderMathIn` no `useEffect`.

import { useEffect, useRef } from "preact/hooks";
import { Check, FileText, Info, ListChecks } from "lucide-react";
import { renderBlock, renderInline } from "../lib/markdown";
import { ensureKaTeX, renderMathIn } from "../lib/math";

// ---------------------------------------------------------------------------
// Shape tolerante. Chaves duplicadas (PT/EN, snake/camel) suportadas — caller
// não precisa normalizar antes de passar.

export type PreviewOpcao = string | { imagem: string; legenda?: string };

export interface PreviewTabela {
  titulo?: string;
  cabecalho?: string[];
  linhas?: string[][];
  nota?: string;
}

export interface PreviewImagem {
  url: string;
  legenda?: string;
}

export interface PreviewItem {
  id?: string;
  label?: string;
  tipo?: "mc" | "essay" | string;
  pergunta?: string;
  enunciado?: string;
  opcoes?: PreviewOpcao[];
  alternativas?: PreviewOpcao[];
  correta?: number; // 1-based (0 = anulada)
  explicacao?: string;
  gabarito_modelo?: string;
  notaQuestao?: number;
  peso?: number;
}

export interface PreviewQuestion {
  id?: string | number;
  /** Numeração no arquivo original (ex: "12", "Q05a"). Aparece como rótulo
   *  pro admin lembrar qual prova/posição esta questão veio. */
  numero_original?: string | number;
  /** Mesmo campo que numero_original, mas com o nome usado em alguns bancos
   *  legados (snake case). Manter a duplicata é seguro porque o backend
   *  prefere `numero_original`. */
  numero_origem?: string | number;
  /** Path/URL/identificador do arquivo de onde foi extraída (PDF, etc). */
  arquivo_origem?: string;
  tipo?: "mc" | "essay" | string;
  tema?: string;
  subtema?: string;
  fonte?: string;
  /** 1..5 (estrelas). 0 ou ausente = não classificada. */
  dificuldade?: number;
  /** Peso na prova agendada (default 1). Frações são OK. */
  notaQuestao?: number;
  /** Tags administrativas (namespace:valor). */
  tags?: string[];
  pergunta?: string;
  enunciado?: string;
  caso?: string;
  imagem_url?: string;
  imagens?: Array<string | PreviewImagem>;
  tabelas?: PreviewTabela[];
  opcoes?: PreviewOpcao[];
  alternativas?: PreviewOpcao[];
  correta?: number; // 1-based (0 = anulada)
  explicacao?: string;
  gabarito?: string; // legado
  gabarito_modelo?: string;
  criterios?: string[];
  competencias?: string[];
  fragmentos?: Array<{ label?: string; textContent?: string }>;
  direcionadores?: string;
  banca?: string;
  genero?: string;
  linhas_min?: number;
  linhas_max?: number;
  nota_maxima?: number;
  items?: PreviewItem[];
}

// ---------------------------------------------------------------------------
// Helpers internos

function getEnunciado(q: PreviewQuestion | PreviewItem): string {
  if (typeof q.pergunta === "string" && q.pergunta) return q.pergunta;
  if (typeof q.enunciado === "string" && q.enunciado) return q.enunciado;
  return "";
}

function getOpcoes(q: PreviewQuestion | PreviewItem): PreviewOpcao[] {
  if (Array.isArray(q.opcoes)) return q.opcoes;
  if (Array.isArray(q.alternativas)) return q.alternativas;
  return [];
}

function getCorrectIdx(q: PreviewQuestion | PreviewItem): number {
  if (typeof q.correta === "number" && q.correta >= 1) return q.correta - 1;
  return -1;
}

function isAnulada(q: PreviewQuestion | PreviewItem): boolean {
  return q.correta === 0;
}

function isImageOpt(opt: PreviewOpcao): opt is { imagem: string; legenda?: string } {
  return typeof opt === "object" && opt !== null && "imagem" in opt;
}

// ---------------------------------------------------------------------------
// QuestionPreview — componente principal

export interface QuestionPreviewProps {
  q: PreviewQuestion;
  /** Mostra o gabarito (alternativa correta destacada + bloco "Explicação").
   *  Default true — prévia normalmente quer ver a resposta. */
  showGabarito?: boolean;
  /** Esconde a badge "Tema" no header. */
  hideTema?: boolean;
  /** Esconde a badge "Dificuldade" no header. */
  hideDificuldade?: boolean;
  /** Callback opcional ao clicar numa imagem (lightbox externo). Sem
   *  callback, imagens ficam clicáveis mas só abrem em nova aba. */
  onImageClick?: (img: PreviewImagem) => void;
}

export function QuestionPreview({
  q,
  showGabarito = true,
  hideTema,
  hideDificuldade,
  onImageClick
}: QuestionPreviewProps) {
  const articleRef = useRef<HTMLElement>(null);
  const composta = Array.isArray(q.items) && q.items.length >= 2;
  const isEssay = !composta && q.tipo === "essay";
  const anulada = isAnulada(q);
  const corrIdx = getCorrectIdx(q);
  const enunciado = getEnunciado(q);

  useEffect(() => {
    void ensureKaTeX().then(() => renderMathIn(articleRef.current));
  }, [q]);

  return (
    <article
      ref={articleRef}
      class="space-y-4 rounded-md border border-border bg-card p-6"
    >
      {/* Badges header (tema, fonte, dificuldade, anulada, numero original) */}
      <HeaderBadges
        q={q}
        hideTema={hideTema}
        hideDificuldade={hideDificuldade}
        anulada={anulada}
      />

      {/* Situação (caso clínico) */}
      {q.caso && (
        <div class="rounded-md border-l-4 border-sky-500/50 bg-sky-500/5 px-4 py-3 text-sm">
          <div class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
            Situação
          </div>
          <div
            class="prose-question text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(q.caso) }}
          />
        </div>
      )}

      {/* Tabelas */}
      {Array.isArray(q.tabelas) && q.tabelas.length > 0 && (
        <div class="space-y-3">
          {q.tabelas.map((t, i) => (
            <PreviewTable key={i} table={t} />
          ))}
        </div>
      )}

      {/* Imagens */}
      <QuestionPreviewImages q={q} onImageClick={onImageClick} />

      {/* Enunciado */}
      {enunciado && (
        <div
          class="prose-question text-base leading-relaxed"
          dangerouslySetInnerHTML={{ __html: renderBlock(enunciado) }}
        />
      )}

      {/* Composta: items[] como sub-cards */}
      {composta && (
        <ol class="space-y-4">
          {(q.items ?? []).map((it) => (
            <li key={it.id ?? it.label ?? Math.random()}>
              <CompositeItemPreview it={it} showGabarito={showGabarito} />
            </li>
          ))}
        </ol>
      )}

      {/* Essay simples — fragmentos, direcionadores e gabarito-modelo */}
      {isEssay && (
        <EssayPreviewBody q={q} showGabarito={showGabarito} />
      )}

      {/* MC simples — alternativas */}
      {!composta && !isEssay && getOpcoes(q).length > 0 && (
        <MCAlternatives
          opcoes={getOpcoes(q)}
          corrIdx={corrIdx}
          showGabarito={showGabarito}
          anulada={anulada}
          onImageClick={onImageClick}
        />
      )}

      {/* Explicação (só quando mostra gabarito) */}
      {showGabarito && !composta && q.explicacao && (
        <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-4 py-3 text-sm">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Info className="size-3" />
            Explicação
          </div>
          <div
            class="prose-question leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(q.explicacao) }}
          />
        </div>
      )}

      {/* Gabarito legado (string crua) — usado em uploads antigos sem
          alternativas estruturadas. */}
      {showGabarito && !composta && !isEssay && getOpcoes(q).length === 0 && q.gabarito && (
        <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
          <div class="mb-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            Gabarito (referência)
          </div>
          <div class="whitespace-pre-wrap">{q.gabarito}</div>
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes

function HeaderBadges({
  q,
  hideTema,
  hideDificuldade,
  anulada
}: {
  q: PreviewQuestion;
  hideTema?: boolean;
  hideDificuldade?: boolean;
  anulada: boolean;
}) {
  const dif = typeof q.dificuldade === "number" ? q.dificuldade : null;
  const numeroOriginal = q.numero_original ?? q.numero_origem;
  const peso = typeof q.notaQuestao === "number" ? q.notaQuestao : null;
  const showAny =
    numeroOriginal != null ||
    (!hideTema && !!q.tema) ||
    !!q.subtema ||
    !!q.fonte ||
    !!q.arquivo_origem ||
    (!hideDificuldade && dif != null) ||
    peso != null ||
    anulada ||
    !!q.tipo;
  if (!showAny) return null;
  return (
    <div class="flex flex-wrap items-center gap-1.5 text-[11px]">
      {numeroOriginal != null && (
        <span
          class="rounded-md border border-border bg-background px-1.5 py-0.5 font-mono text-muted-foreground"
          title={
            q.arquivo_origem
              ? `Origem: ${q.arquivo_origem} (#${numeroOriginal})`
              : `Questão #${numeroOriginal} no arquivo original`
          }
        >
          #{String(numeroOriginal)}
        </span>
      )}
      {q.tipo && q.tipo !== "mc" && (
        <span class="rounded-md border border-violet-500/40 bg-violet-500/10 px-1.5 py-0.5 font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
          {q.tipo === "essay" ? "Dissertativa" : q.tipo}
        </span>
      )}
      {!hideTema && q.tema && (
        <span
          class="rounded-md border border-border bg-background px-1.5 py-0.5 font-medium"
          title={q.subtema ? `${q.tema} → ${q.subtema}` : q.tema}
        >
          {q.tema}
          {q.subtema && (
            <span class="ml-1 text-muted-foreground">· {q.subtema}</span>
          )}
        </span>
      )}
      {q.fonte && (
        <span class="rounded-md border border-border bg-background px-1.5 py-0.5 text-muted-foreground">
          {q.fonte}
        </span>
      )}
      {q.arquivo_origem && !q.fonte && (
        <span
          class="rounded-md border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
          title="Arquivo de origem"
        >
          {q.arquivo_origem}
        </span>
      )}
      {!hideDificuldade && dif != null && dif > 0 && (
        <span class="rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-bold tabular-nums text-amber-700 dark:text-amber-300">
          {"★".repeat(Math.min(5, dif))}
        </span>
      )}
      {peso != null && peso !== 1 && peso > 0 && (
        <span
          class="rounded-md border border-blue-500/40 bg-blue-500/10 px-1.5 py-0.5 font-bold tabular-nums text-blue-700 dark:text-blue-300"
          title="Peso na prova agendada"
        >
          peso {peso}
        </span>
      )}
      {anulada && (
        <span class="rounded-md border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
          Anulada
        </span>
      )}
    </div>
  );
}

function PreviewTable({ table }: { table: PreviewTabela }) {
  if (!table || typeof table !== "object") return null;
  return (
    <div class="overflow-x-auto rounded-md border border-border bg-card/60">
      {table.titulo && (
        <div
          class="border-b border-border bg-accent/20 px-3 py-2 text-xs font-bold"
          dangerouslySetInnerHTML={{ __html: renderInline(table.titulo) }}
        />
      )}
      <table class="w-full text-sm">
        {Array.isArray(table.cabecalho) && table.cabecalho.length > 0 && (
          <thead>
            <tr class="border-b border-border bg-accent/10">
              {table.cabecalho.map((h, i) => (
                <th
                  key={i}
                  class="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: renderInline(h) }}
                />
              ))}
            </tr>
          </thead>
        )}
        {Array.isArray(table.linhas) && table.linhas.length > 0 && (
          <tbody>
            {table.linhas.map((row, i) => (
              <tr key={i} class="border-b border-border last:border-b-0">
                {row.map((cell, j) => (
                  <td
                    key={j}
                    class="px-3 py-2"
                    dangerouslySetInnerHTML={{ __html: renderInline(cell) }}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
      {table.nota && (
        <div
          class="border-t border-border bg-card/40 px-3 py-2 text-[11px] italic text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: renderInline(table.nota) }}
        />
      )}
    </div>
  );
}

function QuestionPreviewImages({
  q,
  onImageClick
}: {
  q: PreviewQuestion;
  onImageClick?: (img: PreviewImagem) => void;
}) {
  const imgs: PreviewImagem[] = [];
  if (typeof q.imagem_url === "string" && q.imagem_url) {
    imgs.push({ url: q.imagem_url });
  }
  if (Array.isArray(q.imagens)) {
    for (const it of q.imagens) {
      if (typeof it === "string") imgs.push({ url: it });
      else if (it && typeof it === "object" && typeof it.url === "string") {
        imgs.push({ url: it.url, legenda: it.legenda });
      }
    }
  }
  if (imgs.length === 0) return null;
  const single = imgs.length === 1;
  return (
    <div
      class={`mx-auto grid gap-3 ${
        single ? "max-w-md" : "max-w-2xl sm:grid-cols-2"
      }`}
    >
      {imgs.map((im, i) => (
        <figure
          key={i}
          class="overflow-hidden rounded-md border border-border bg-accent/20"
        >
          <button
            type="button"
            onClick={() => {
              if (onImageClick) onImageClick(im);
              else window.open(im.url, "_blank", "noopener");
            }}
            class="block w-full text-left transition-opacity hover:opacity-90"
            title="Clique para ampliar"
          >
            <img
              src={im.url}
              alt={im.legenda ?? `Imagem ${i + 1}`}
              class="mx-auto block w-full cursor-zoom-in"
              loading="lazy"
            />
          </button>
          {im.legenda && (
            <figcaption
              class="border-t border-border bg-card/60 px-3 py-2 text-center text-xs text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: renderInline(im.legenda) }}
            />
          )}
        </figure>
      ))}
    </div>
  );
}

function MCAlternatives({
  opcoes,
  corrIdx,
  showGabarito,
  anulada,
  onImageClick
}: {
  opcoes: PreviewOpcao[];
  corrIdx: number;
  showGabarito: boolean;
  anulada: boolean;
  onImageClick?: (img: PreviewImagem) => void;
}) {
  return (
    <ul class="space-y-2">
      {opcoes.map((opt, i) => {
        const letter = String.fromCharCode(65 + i);
        const isCorrectAlt = i === corrIdx;
        const highlight = showGabarito && !anulada && isCorrectAlt;
        const classes = anulada
          ? "border-border bg-card/30 opacity-60"
          : highlight
            ? "border-emerald-500 bg-emerald-500/20 ring-2 ring-emerald-500/40"
            : "border-border bg-card/60";
        const badgeClasses = highlight
          ? "border-emerald-500 bg-emerald-500 text-white"
          : "border-border bg-background";
        const imageOpt = isImageOpt(opt);
        return (
          <li key={i}>
            <div
              class={`flex w-full ${
                imageOpt ? "items-start" : "items-center"
              } gap-3 rounded-md border px-4 py-3 text-left text-sm transition-colors ${classes}`}
            >
              <span
                class={`grid size-7 shrink-0 place-items-center rounded-full border text-xs font-bold ${badgeClasses}`}
              >
                {letter}
              </span>
              {imageOpt ? (
                <AltImageBlock
                  src={opt.imagem}
                  legenda={opt.legenda}
                  onImageClick={onImageClick}
                />
              ) : (
                <span
                  class="flex-1 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: renderInline(String(opt ?? ""))
                  }}
                />
              )}
              {highlight && (
                <Check className="size-5 shrink-0 text-emerald-500" strokeWidth={3} />
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function AltImageBlock({
  src,
  legenda,
  onImageClick
}: {
  src: string;
  legenda?: string;
  onImageClick?: (img: PreviewImagem) => void;
}) {
  return (
    <span class="flex min-w-0 flex-1 flex-col gap-1">
      <img
        src={src}
        alt={legenda ?? "Alternativa"}
        loading="lazy"
        onClick={(e) => {
          e.stopPropagation();
          if (onImageClick) onImageClick({ url: src, legenda });
          else window.open(src, "_blank", "noopener");
        }}
        class="block max-h-64 w-auto cursor-zoom-in rounded-md border border-border bg-background object-contain"
      />
      {legenda && (
        <span
          class="text-[11px] text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: renderInline(legenda) }}
        />
      )}
    </span>
  );
}

function CompositeItemPreview({
  it,
  showGabarito
}: {
  it: PreviewItem;
  showGabarito: boolean;
}) {
  const isEssay = it.tipo === "essay";
  const corrIdx = getCorrectIdx(it);
  const anulada = isAnulada(it);
  const enunciado = getEnunciado(it);
  const opcoes = getOpcoes(it);
  const accent = isEssay
    ? "border-violet-500/30 bg-violet-500/5"
    : "border-blue-500/30 bg-blue-500/5";
  const labelBadge = isEssay
    ? "bg-violet-500/20 text-violet-700 dark:text-violet-300"
    : "bg-blue-500/20 text-blue-700 dark:text-blue-300";
  return (
    <article class={`space-y-3 rounded-md border p-4 ${accent}`}>
      <header class="flex flex-wrap items-center gap-2">
        {it.label && (
          <span class={`grid size-7 shrink-0 place-items-center rounded-md text-xs font-bold ${labelBadge}`}>
            {it.label}
          </span>
        )}
        <span
          class={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
            isEssay
              ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
              : "bg-blue-500/15 text-blue-700 dark:text-blue-300"
          }`}
        >
          {isEssay ? "Dissertativa" : "Múltipla escolha"}
        </span>
        {anulada && (
          <span class="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
            Anulada
          </span>
        )}
        {typeof it.notaQuestao === "number" && it.notaQuestao > 0 && (
          <span class="ml-auto text-[11px] text-muted-foreground">
            Peso {it.notaQuestao}
          </span>
        )}
      </header>
      {enunciado && (
        <div
          class="prose-question text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: renderBlock(enunciado) }}
        />
      )}
      {isEssay
        ? showGabarito && it.gabarito_modelo && (
            <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-3 py-2 text-sm">
              <div class="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <Info className="size-3" />
                Gabarito-modelo
              </div>
              <div
                class="prose-question leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderBlock(it.gabarito_modelo) }}
              />
            </div>
          )
        : opcoes.length > 0 && (
            <MCAlternatives
              opcoes={opcoes}
              corrIdx={corrIdx}
              showGabarito={showGabarito}
              anulada={anulada}
            />
          )}
      {showGabarito && !isEssay && it.explicacao && (
        <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-3 py-2 text-sm">
          <div class="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Info className="size-3" />
            Explicação do item
          </div>
          <div
            class="prose-question leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(it.explicacao) }}
          />
        </div>
      )}
    </article>
  );
}

function EssayPreviewBody({
  q,
  showGabarito
}: {
  q: PreviewQuestion;
  showGabarito: boolean;
}) {
  const fragmentos = Array.isArray(q.fragmentos) ? q.fragmentos : [];
  const criterios = Array.isArray(q.criterios) ? q.criterios : [];
  const competencias = Array.isArray(q.competencias) ? q.competencias : [];
  return (
    <div class="space-y-4">
      {(q.banca || q.genero || q.linhas_min || q.linhas_max || q.nota_maxima) && (
        <div class="flex flex-wrap items-center gap-2 text-[11px]">
          {q.banca && (
            <span class="rounded-md bg-violet-500/15 px-2 py-0.5 font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              {q.banca}
            </span>
          )}
          {q.genero && (
            <span class="rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground">
              {q.genero}
            </span>
          )}
          {(q.linhas_min || q.linhas_max) && (
            <span class="rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground">
              {q.linhas_min && q.linhas_max
                ? `${q.linhas_min}–${q.linhas_max} linhas`
                : q.linhas_max
                  ? `até ${q.linhas_max} linhas`
                  : `mín. ${q.linhas_min} linhas`}
            </span>
          )}
          {typeof q.nota_maxima === "number" && q.nota_maxima > 0 && (
            <span class="rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground">
              Nota máx. {q.nota_maxima}
            </span>
          )}
        </div>
      )}

      {fragmentos.length > 0 && (
        <div class="space-y-2">
          <div class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <FileText className="size-3" />
            Textos motivadores ({fragmentos.length})
          </div>
          <div class="space-y-2">
            {fragmentos.map((f, i) => (
              <div
                key={i}
                class="rounded-md border-l-4 border-violet-500/40 bg-violet-500/5 px-4 py-3"
              >
                {f.label && (
                  <div class="mb-1 text-[11px] font-bold text-violet-700 dark:text-violet-300">
                    {f.label}
                  </div>
                )}
                <div
                  class="prose-question text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: renderBlock(f.textContent ?? "") }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {q.direcionadores && (
        <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
            <ListChecks className="size-3" />
            Instruções da proposta
          </div>
          <div
            class="prose-question text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(q.direcionadores) }}
          />
        </div>
      )}

      {showGabarito && q.gabarito_modelo && (
        <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-4 py-3 text-sm">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Info className="size-3" />
            Gabarito-modelo
          </div>
          <div
            class="prose-question leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(q.gabarito_modelo) }}
          />
        </div>
      )}
      {showGabarito && (criterios.length > 0 || competencias.length > 0) && (
        <div class="rounded-md border border-border bg-card/40 px-4 py-3 text-sm">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <ListChecks className="size-3" />
            Critérios de correção
          </div>
          {criterios.length > 0 && (
            <ul class="ml-4 list-disc space-y-1 text-[13px] leading-snug">
              {criterios.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          )}
          {competencias.length > 0 && (
            <>
              <div class="mt-3 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Competências adicionais
              </div>
              <ul class="ml-4 list-disc space-y-1 text-[13px] leading-snug">
                {competencias.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
